import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildOperationalControlReadModel } from './operational-control-aggregator';
import type {
  OperationalControlReadModel,
  OperationalFilterKey
} from './types';

type OperationalControlFilters = {
  vpId: string;
  status: string;
  filter: OperationalFilterKey;
  search: string;
};

type UseOperationalControlCenterResult = {
  data: OperationalControlReadModel | null;
  loading: boolean;
  error: string | null;
  filters: OperationalControlFilters;
  setFilters: (next: Partial<OperationalControlFilters>) => void;
  refresh: () => Promise<void>;
  filteredRows: OperationalControlReadModel['rows'];
  vpOptions: Array<{ id: string; name: string }>;
};

const defaultFilters: OperationalControlFilters = {
  vpId: 'ALL',
  status: 'ALL',
  filter: 'all',
  search: ''
};

export const useOperationalControlCenter = (
  user: any,
  selectedMonth: string,
  year = 2026
): UseOperationalControlCenterResult => {
  const [data, setData] = useState<OperationalControlReadModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<OperationalControlFilters>(defaultFilters);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const model = await buildOperationalControlReadModel({ user, selectedMonth, year });
      setData(model);
    } catch (err: any) {
      console.error('[OperationalControl] load error', err);
      setError(err?.message || 'No fue posible cargar Control Operativo.');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, user, year]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setFilters = useCallback((next: Partial<OperationalControlFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...next }));
  }, []);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const search = filters.search.trim().toUpperCase();
    return data.rows.filter((row) => {
      if (filters.vpId !== 'ALL' && row.vpId !== filters.vpId) return false;
      if (filters.status !== 'ALL' && row.operationalStatus !== filters.status) return false;
      if (search && !`${row.instanceName} ${row.vpName}`.toUpperCase().includes(search)) return false;

      switch (filters.filter) {
        case 'critical':
          return row.operationalStatus === 'CRITICO';
        case 'onboarding':
          return row.operationalStatus === 'ONBOARDING';
        case 'noCurrentCapture':
          return row.resolvedWindow.isOperationallyActive && !row.currentMonthCapture;
        case 'stale2':
          return row.staleMonthsOperational >= 2;
        case 'stale3':
          return row.staleMonthsOperational >= 3;
        case 'kpisWithoutGoal':
          return row.kpiTotal > row.kpiWithGoal;
        case 'incompletePlans':
          return !row.planComplete;
        default:
          return true;
      }
    });
  }, [data, filters]);

  const vpOptions = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, string>();
    data.rows.forEach((row) => {
      if (row.vpId) map.set(row.vpId, row.vpName);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  return {
    data,
    loading,
    error,
    filters,
    setFilters,
    refresh,
    filteredRows,
    vpOptions
  };
};
